/**
 * A stable colour per brand.
 *
 * Switching brand re-scopes every number on screen, so the operator must never
 * have to read a label to know which brand they are in. Each brand gets a hue,
 * derived from its id so it is the same on every machine and every session, and
 * that hue shows up as the rule under the top bar, the dot in the switcher and
 * the chip beside cross-brand rows.
 *
 * Class strings are written out in full so Tailwind's scanner finds them.
 */

export interface BrandTone {
  key: string
  /** Solid fill, for the dot and the top rule. */
  bar: string
  dot: string
  /** Soft chip: background, text and border together. */
  chip: string
  /** Ring for the active row in the switcher. */
  ring: string
  text: string
}

const TONES: BrandTone[] = [
  {
    key: 'violet',
    bar: 'bg-violet-500',
    dot: 'bg-violet-500',
    chip: 'bg-violet-50 text-violet-700 border-violet-200',
    ring: 'ring-violet-400/60',
    text: 'text-violet-600',
  },
  {
    key: 'sky',
    bar: 'bg-sky-500',
    dot: 'bg-sky-500',
    chip: 'bg-sky-50 text-sky-700 border-sky-200',
    ring: 'ring-sky-400/60',
    text: 'text-sky-600',
  },
  {
    key: 'emerald',
    bar: 'bg-emerald-500',
    dot: 'bg-emerald-500',
    chip: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    ring: 'ring-emerald-400/60',
    text: 'text-emerald-600',
  },
  {
    key: 'amber',
    bar: 'bg-amber-500',
    dot: 'bg-amber-500',
    chip: 'bg-amber-50 text-amber-800 border-amber-200',
    ring: 'ring-amber-400/60',
    text: 'text-amber-700',
  },
  {
    key: 'rose',
    bar: 'bg-rose-500',
    dot: 'bg-rose-500',
    chip: 'bg-rose-50 text-rose-700 border-rose-200',
    ring: 'ring-rose-400/60',
    text: 'text-rose-600',
  },
  {
    key: 'teal',
    bar: 'bg-teal-500',
    dot: 'bg-teal-500',
    chip: 'bg-teal-50 text-teal-700 border-teal-200',
    ring: 'ring-teal-400/60',
    text: 'text-teal-600',
  },
  {
    key: 'indigo',
    bar: 'bg-indigo-500',
    dot: 'bg-indigo-500',
    chip: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    ring: 'ring-indigo-400/60',
    text: 'text-indigo-600',
  },
  {
    key: 'fuchsia',
    bar: 'bg-fuchsia-500',
    dot: 'bg-fuchsia-500',
    chip: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200',
    ring: 'ring-fuchsia-400/60',
    text: 'text-fuchsia-600',
  },
]

const NEUTRAL: BrandTone = {
  key: 'neutral',
  bar: 'bg-line-strong',
  dot: 'bg-line-strong',
  chip: 'bg-surface-muted text-ink-muted border-line',
  ring: 'ring-line-strong',
  text: 'text-ink-muted',
}

/** Deterministic across sessions and machines: same brand, same colour, always. */
export function brandTone(brandId: string | null | undefined): BrandTone {
  if (!brandId) return NEUTRAL
  let hash = 0
  for (let i = 0; i < brandId.length; i += 1) {
    hash = (hash * 31 + brandId.charCodeAt(i)) >>> 0
  }
  return TONES[hash % TONES.length]
}
